'use client';

import { useContext, useMemo } from 'react';
import { AnalyticsContext } from './AnalyticsContext';
import type { GroupTraits, JsonMap, JsonValue, UserTraits } from './analytics';

export type TrackEventProperties = {
  activityTime?: number;
  activityType?: string;
  activityCategory?: string;
  assetType?: string;
  assetId?: string;
  metrics?: { duration: number };
  screen?: { pageId: string };
  attributes?: JsonMap;
  [key: string]: JsonValue;
};

export function useAnalytics() {
  const analyticsClient = useContext(AnalyticsContext);

  const { track, screen, identify, heartbeat, flush, group, alias, reset } = useMemo(() => {
    return {
      track: (event: string, properties?: TrackEventProperties) => {
        void analyticsClient?.track(event, properties);
      },
      screen: (name: string, properties?: JsonMap) => {
        void analyticsClient?.screen(name, properties);
      },
      identify: (userId?: string, userTraits?: UserTraits) => {
        void analyticsClient?.identify(userId, userTraits);
      },
      heartbeat: (properties?: JsonMap | (() => JsonMap)) => {
        void analyticsClient?.heartbeat(properties);
      },
      flush: () => {
        void analyticsClient?.flush();
      },
      group: (groupId: string, groupTraits?: GroupTraits) => {
        void analyticsClient?.group(groupId, groupTraits);
      },
      alias: (newUserId: string) => {
        void analyticsClient?.alias(newUserId);
      },
      reset: (resetAnonymousId?: boolean) => {
        void analyticsClient?.reset(resetAnonymousId);
      },
    };
  }, [analyticsClient]);

  return {
    track,
    screen,
    identify,
    heartbeat,
    flush,
    group,
    alias,
    reset,
  };
}
